import {css, CSSProperties, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import * as Codons from 'biojs/Codons';
import {StandardDNACodon} from 'biojs/Codons';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import * as SequenceLib from 'components/Sequence';
import DataConnector from 'connect/DataConnector';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {Strand} from 'constants/MolbioConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelectorModalClass from 'file-browser/FolderSelectorModal';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import HelpTooltip from 'help/HelpTooltip';
import {OptimizeCodonsResult} from 'molbio/codon-optimization/CodonOptimizationConstants';
import * as CodonOptimizationTabReduxActions from 'molbio/codon-optimization/CodonOptimizationTabReduxActions';
import {
  getCasedBasesToSave,
  getNotePartsForCodonOptimizedSequence,
  getOrientedBases,
} from 'molbio/codon-optimization/CodonOptimizationUtil';
import {CodonOptimizationTabsSelector} from 'molbio/codon-optimization/reduceAllCodonOptimizationTabs';
import * as SequenceDAO from 'molbio/dao/SequenceDAO';
import * as sequtil from 'molbio/SeqUtil';
import {borderStyle} from 'molbio/SharedStyles';
import * as RegistryUtil from 'registry/RegistryUtil';
import {
  FONT_FAMILY_MONOSPACED,
  LEGACY_FONT_PROXIMA_13,
  LEGACY_FONT_PROXIMA_TITLE,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {Title} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {InputChangeEvent} from 'util/Types';

const CODON_CHANGES_HEIGHT = 15;

function Heading({children}: {children: ReactNode}): JSX.Element {
  return <th className={css(styles.tableHeading)}>{children}</th>;
}

export function Cell({children}: {children?: ReactNode}): JSX.Element {
  return <td className={css(styles.tableCell)}>{children}</td>;
}

function TableTitle({children}: {children?: ReactNode}): JSX.Element {
  return <td className={css(styles.tableTitle)}>{children}</td>;
}

function asPercentage(num: number): string {
  return `${Math.round(num * 100).toString()}%`;
}

export function codonChange(codon: StandardDNACodon, codonFreq: number | null): JSX.Element {
  return (
    <div
      className={css(styles.codonChange)}
      {...getTestProps({component: 'CodonOptimizationTabPreview', element: 'codonChange'})}
    >
      <span className={css(styles.codon)}>
        {codon}
        <BenchlingIcon className={css(styles.icon)} icon="arrow-right" />
        {Codons.STANDARD_DNA_CODE[codon]}
      </span>
      {codonFreq && <span>{` (${codonFreq})`}</span>}
    </div>
  );
}

class CodonOptimizationTabPreviewConnector extends DataConnector<
  CodonOptimizationTabPreviewProps,
  EnhancedProps
> {
  enhanceProps(): EnhancedProps {
    const selector = new CodonOptimizationTabsSelector(this.reduxState());
    const state = selector.getState(this.props.tabId);
    assert(state != null, 'Expected tab state to exist');
    assert(
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      state.optimizationStart !== null && state.optimizationEnd !== null,
      'Expected optimizationStart and optimizationEnd to be set.'
    );
    const optimizationStart = state.optimizationStart;
    const optimizationEnd = state.optimizationEnd;
    let onSave: (() => void) | null;
    if (this.props.isBackTranslation) {
      onSave = null;
    } else {
      assert(
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        this.props.onReplaceSequence !== null,
        'onReplaceSequence must be specified when codon optimizing a DNA sequence.'
      );
      const onReplaceSequence = this.props.onReplaceSequence as (
        start: number,
        end: number,
        bases: string,
        options?: {callback: () => void}
      ) => void;
      onSave = (): void => {
        assert(!this.props.isBackTranslation, 'Cannot save codon optimized sequence from back translation.');
        onReplaceSequence(
          optimizationStart,
          optimizationEnd,
          getCasedBasesToSave(state.bases, this.props.result.optimizedBases, this.props.strand)
        );
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.SAVED_CODON_OPTIMIZED_SEQUENCE, {
          type: 'sequence',
          polymerId: this.props.sequenceId,
        });
        this.props.onClose();
      };
    }
    return {
      optimizationStart,
      optimizationEnd,
      onRequestSubmit: async (): Promise<void> => {
        const [start, end] = sequtil.modelRangeToDisplay(
          optimizationStart,
          optimizationEnd,
          this.props.seqLength,
          this.props.startIndex
        );
        const noteParts = getNotePartsForCodonOptimizedSequence({
          isBackTranslation: this.props.isBackTranslation,
          originalEntityId: this.props.sequenceId,
          start,
          end,
          strand: this.props.strand,
          organism: state.selectedOrganism,
          gcLevel: state.selectedGCLevel,
          uridineDepletion: state.uridineDepletion,
          avoidHairpins: state.avoidHairpins,
          hairpinStem: state.hairpinStem,
          hairpinWindow: state.hairpinWindow,
          protectedRegions: state.protectedRegions,
          cutsitesToAvoid: state.cutsitesToAvoid,
          cutsitesToPreserve: state.cutsitesToPreserve,
          patternsToReduce: state.patternsToReduce,
          optimizationMetrics: state.result?.optimizationMetrics ?? null,
          method: state.method,
          gcContent: state.gcContent,
        });
        if (this.props.isBackTranslation) {
          FolderSelectorModalClass.openForSelect(
            'append',
            {showInventory: true, initialFolderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY)},
            async (e: InputChangeEvent) => {
              LoadingScreen.show();
              const {folderId} = RegistryUtil.parseFolderValue(e.target.value);
              const optimizedSequence = await SequenceDAO.create({
                name: `${this.props.name} (codon optimized)`,
                folder: folderId!,
                circular: false,
                bases: getOrientedBases(this.props.result.optimizedBases, this.props.strand).toUpperCase(),
                noteParts,
              });
              await FileStates.get().openFolderItem(optimizedSequence.id);
              FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId!);
              FolderDAOCache.invalidateFolderContents(folderId);
              LoadingScreen.hide();
              analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.CREATED_CODON_OPTIMIZED_SEQUENCE, {
                type: 'protein',
                polymerId: this.props.sequenceId,
              });
              this.props.onClose();
            }
          );
        } else {
          const createFromSelection = this.props.createFromSelection!;
          await SequenceLib.Sequence.saveAndOpenSequence(
            createFromSelection(
              getCasedBasesToSave(state.bases, this.props.result.optimizedBases, this.props.strand),
              `${this.props.name} (codon optimized)`
            ),
            null,
            {
              noteParts,
            }
          );
          analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.CREATED_CODON_OPTIMIZED_SEQUENCE, {
            type: 'sequence',
            polymerId: this.props.sequenceId,
          });
          this.props.onClose();
        }
      },
      onBack: (): void => {
        this.dispatch(CodonOptimizationTabReduxActions.goBack(this.props.tabId));
      },
      onSave,
    };
  }
}

interface EnhancedProps {
  onRequestSubmit: () => void;
  onBack: () => void;
  onSave: (() => void) | null;
  optimizationStart: number;
  optimizationEnd: number;
}

export interface CodonOptimizationTabPreviewProps {
  result: OptimizeCodonsResult;
  onClose: () => void;
  tabId: string;
  name: string;
  onReplaceSequence:
    | null
    | ((start: number, end: number, bases: string, options?: {callback: () => void}) => void);
  startIndex: number;
  sequenceId: string;
  circular: boolean;
  seqLength: number;
  isRegionEditable: boolean;
  isBackTranslation: boolean;
  strand: Strand;
  seqEnd: number | null;
  createFromSelection: ((bases: string, name: string) => BioJSSequence) | null;
}

const CodonOptimizationTabPreview = (
  props: CodonOptimizationTabPreviewProps & EnhancedProps
): JSX.Element => {
  const optimizationStart = props.optimizationStart;
  const optimizationLength = sequtil.length(optimizationStart, props.optimizationEnd, props.seqLength);
  let columns: Array<ScrollableTableColumn>;
  let rows;
  if (!props.isBackTranslation) {
    rows = props.result.changes.map((change) => ({
      id: change.location.toString(),
      data: {
        location: sequtil.modelToDisplay(
          (optimizationStart +
            (props.strand === 1 || props.strand === 0
              ? change.location
              : optimizationLength - change.location)) %
            props.seqLength,
          props.startIndex
        ),
        original: change.codonFreqs
          ? codonChange(change.codons.original, change.codonFreqs.original)
          : codonChange(change.codons.original, null),
        optimized: change.codonFreqs
          ? codonChange(change.codons.optimized, change.codonFreqs.optimized)
          : codonChange(change.codons.optimized, null),
      },
    }));
    columns = [
      {
        header: 'Location',
        accessorKey: 'location',
        flexGrow: 0,
      },
      {
        header: 'Original',
        accessorKey: 'original',
        flexGrow: 1,
      },
      {
        header: 'Optimized',
        accessorKey: 'optimized',
        flexGrow: 1,
      },
    ];
  }

  return (
    <div
      className={css(styles.tab)}
      {...getTestProps({component: 'CodonOptimizationTabPreview', element: 'root'})}
    >
      <div className={css(styles.header)}>
        <Title>Optimization preview</Title>
        <p className={css(styles.modifiedText)}>
          This is a summary of the {props.isBackTranslation ? 'back-translated DNA' : 'optimized'} sequence.
          You can save this sequence or go back and modify your optimization parameters.
        </p>
      </div>
      <div className={css(styles.body)}>
        <table className={css(styles.table)}>
          <thead>
            <tr>
              <TableTitle>Metric</TableTitle>
              {!props.isBackTranslation && <Heading>Before</Heading>}
              <Heading>{props.isBackTranslation ? '' : 'After'}</Heading>
            </tr>
          </thead>
          <tbody>
            {props.result.optimizationMetrics.numRareCodons && (
              <tr>
                <Cell>
                  <div className={css(styles.helperCell)}>
                    <div>Rare codons</div>
                    <HelpTooltip
                      className={css(styles.helperIcon)}
                      text="We define a rare codon as one that accounts for less than 10% of all codons mapping to the corresponding amino acid for a given organism"
                    />
                  </div>
                </Cell>
                {!props.isBackTranslation && (
                  <Cell>{props.result.optimizationMetrics.numRareCodons.original}</Cell>
                )}
                <Cell>{props.result.optimizationMetrics.numRareCodons.optimized}</Cell>
              </tr>
            )}
            <tr>
              <Cell>GC content</Cell>
              {!props.isBackTranslation && (
                <Cell>{asPercentage(props.result.optimizationMetrics.gcContent.original)}</Cell>
              )}
              <Cell>{asPercentage(props.result.optimizationMetrics.gcContent.optimized)}</Cell>
            </tr>
            <tr>
              <Cell>Uridine content</Cell>
              {!props.isBackTranslation && (
                <Cell>{asPercentage(props.result.optimizationMetrics.thymineContent.original)}</Cell>
              )}
              <Cell>{asPercentage(props.result.optimizationMetrics.thymineContent.optimized)}</Cell>
            </tr>
            <tr>
              <Cell>
                <div className={css(styles.helperCell)}>
                  <div>Hairpins</div>
                  <HelpTooltip
                    className={css(styles.helperIcon)}
                    text="See previous step for explanation of hairpin parameters"
                  />
                </div>
              </Cell>
              {!props.isBackTranslation && (
                <Cell>{props.result.optimizationMetrics.numHairpins.original}</Cell>
              )}
              <Cell>{props.result.optimizationMetrics.numHairpins.optimized}</Cell>
            </tr>
            {props.result.optimizationMetrics.patterns &&
              props.result.optimizationMetrics.patterns.map((patternMetric) => (
                <tr key={patternMetric.pattern}>
                  <Cell>
                    <div className={css(styles.helperCell)}>
                      <div>
                        Occurrences: <i>{patternMetric.pattern}</i>
                      </div>
                      <HelpTooltip
                        className={css(styles.helperIcon)}
                        text="This pattern was specified by the user. The optimization procedure attempted to reduce its occurrence."
                      />
                    </div>
                  </Cell>
                  {!props.isBackTranslation && <Cell>{patternMetric.original}</Cell>}
                  <Cell>{patternMetric.optimized}</Cell>
                </tr>
              ))}
          </tbody>
        </table>
        <br />
        {!props.isBackTranslation && rows && (
          <ScrollableTable
            columns={columns!}
            hasLineBelowHeader={true}
            rows={rows}
            getRowContentHeight={(): number => CODON_CHANGES_HEIGHT}
            emptyTableText="No codons were modified."
          />
        )}
      </div>
      <div className={css(styles.buttonContainer)}>
        <Button
          onClick={props.onBack}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MEDIUM}
          text="Back"
          className={css(styles.backButton)}
        />
        <Button
          onClick={props.onRequestSubmit}
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MEDIUM}
          text="Save as new sequence"
          className={css(styles.saveNewButton)}
          dataTestAttrs={{component: 'CodonOptimizationTabPreview', element: 'saveAsNewSequence'}}
        />
        {props.isRegionEditable && !props.isBackTranslation && (
          <Button
            onClick={props.onSave as () => void}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MEDIUM}
            text="Save"
            className={css(styles.saveButton)}
          />
        )}
      </div>
    </div>
  );
};

export default CodonOptimizationTabPreviewConnector.connect(CodonOptimizationTabPreview);

const CELL_STYLES: CSSProperties = {
  paddingLeft: 10,
  paddingTop: 5,
  paddingBottom: 5,
};

const HEAD_STYLES: CSSProperties = {
  borderBottom: borderStyle,
  ...LEGACY_FONT_PROXIMA_TITLE,
};

const styles = StyleSheet.create({
  modifiedText: {
    color: GRAY_DARKER,
    paddingBottom: 20,
  },
  saveButton: {
    float: 'right',
    marginTop: 10,
    marginRight: 3,
    marginBottom: 10,
  },
  saveNewButton: {
    float: 'right',
    marginTop: 10,
    marginLeft: 3,
    marginBottom: 10,
    marginRight: 10,
  },
  backButton: {
    float: 'left',
    margin: 10,
  },
  tableCell: {
    ...LEGACY_FONT_PROXIMA_13,
    ...CELL_STYLES,
    color: GRAY_DARKER,
  },
  tableHeading: {
    ...CELL_STYLES,
    ...HEAD_STYLES,
    color: GRAY_DARKER,
  },
  tableTitle: {
    ...CELL_STYLES,
    ...HEAD_STYLES,
  },
  table: {
    width: '100%',
  },
  codon: {
    fontFamily: FONT_FAMILY_MONOSPACED,
    letterSpacing: 1.6,
    fontWeight: 'bold',
  },
  tab: {
    width: '100%',
    height: '100%',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
  },
  icon: {
    height: CODON_CHANGES_HEIGHT - 5,
    marginLeft: 3,
    marginRight: 3,
  },
  codonChange: {
    paddingTop: 2,
  },
  header: {
    height: 70,
  },
  body: {
    overflowY: 'auto',
  },
  buttonContainer: {
    height: 50,
    flex: 'none',
    marginTop: 'auto',
  },
  helperCell: {
    display: 'flex',
    alignItems: 'center',
  },
  helperIcon: {
    marginLeft: '6px',
  },
});
